import { forwardRef, Fragment, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import cx from 'classnames';
import { setAlpha } from '../../design-tokens';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { useResizeObserverRef } from '../../hooks/useResizeObserver';
import { useIsRTL } from '../../locale';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { useFormatLocale } from '../../locale/formatting';
import { useNCTextLocalizer } from '../../locale/useNCTextLocalizer';
import { renderDesignTokens, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { calculateSliderStep, getSliderTicks } from './__internal__/utils';
import { SliderRange, SliderRangeGrip } from './RangeGrip';
import { calculatePreciseValue, SliderDefaultFormat } from './SliderFormat';
import { LinearSliderScale } from './SliderScale';
import { GripType, RangeSliderType, SliderType } from './SliderValues';
import { SliderTickLabels, SliderTrackTicks } from './Ticks';
import { SliderValueGrip } from './ValueGrip';
import bundle from './NovaComponents-Slider-gui-icu.properties';
import cn from './Slider.css.json';
import './Slider.css';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
createUseTextLocalizer(bundle);
const defaultScale = LinearSliderScale.createFromMinMax(0, 100, false);
export const Slider = /*#__PURE__*/forwardRef(function Slider({
  ...props
}, ref) {
  return /*#__PURE__*/_jsx(BaseSlider, {
    ...props,
    ref: ref,
    values: [{
      type: 'value',
      valueType: props.valueType,
      value: props.value,
      exclusive: props.exclusive,
      onChange: props.onChange,
      onCommit: props.onCommit
    }],
    gripAriaText: props.gripAriaText
  });
});
export const RangeSlider = /*#__PURE__*/forwardRef(function RangeSlider({
  ...props
}, ref) {
  const [sliderGripControlIsVisible, setSliderGripControlIsVisible] = useState(false);
  const [hasMinSliderInputErrorOrFocus, setHasMinSliderInputErrorOrFocus] = useState(false);
  const [hasMaxSliderInputErrorOrFocus, setHasMaxSliderInputErrorOrFocus] = useState(false);
  useEffect(() => {
    if (hasMinSliderInputErrorOrFocus || hasMaxSliderInputErrorOrFocus) setSliderGripControlIsVisible(true);else setSliderGripControlIsVisible(false);
  }, [hasMinSliderInputErrorOrFocus, hasMaxSliderInputErrorOrFocus]);
  return /*#__PURE__*/_jsx(BaseSlider, {
    ...props,
    ref: ref,
    sliderGripControlIsVisible: sliderGripControlIsVisible,
    setSliderGripControlIsVisible: setSliderGripControlIsVisible,
    setHasMinSliderInputErrorOrFocus: setHasMinSliderInputErrorOrFocus,
    setHasMaxSliderInputErrorOrFocus: setHasMaxSliderInputErrorOrFocus,
    values: [{
      type: 'range',
      valueType: props.valueType,
      minValue: props.minValue,
      maxValue: props.maxValue,
      minExclusive: props.minExclusive,
      maxExclusive: props.maxExclusive,
      onChange: props.onChange,
      onCommit: props.onCommit
    }],
    minGripAriaText: props.minGripAriaText,
    maxGripAriaText: props.maxGripAriaText,
    rangeAriaText: props.rangeAriaText
  });
});
const useHtmlId = createUseHtmlId('sliderTickLabels');
class SnapPrecisionScale {
  constructor(scale, step, numberFormat, disableRoundToStep, isDateType) {
    this._scale = scale;
    this._step = step ?? 1;
    this._numberFormat = numberFormat ?? new Intl.NumberFormat(undefined, {
      maximumFractionDigits: 20
    });
    this._disableRoundToStep = disableRoundToStep;
    this._isDateType = isDateType;
  }
  value(position, didDrag) {
    const value = this._scale.value(position);

    // We should only disable step rounding if the following conditions are met:
    // 1. disableRoundToStep is set to true AND
    // 2. we are not dragging OR
    // 3. we are dragging but we're a date slider or at the min or max boundary
    //
    // note that we do not disable step rounding if the user is dragging,
    // even if props.disableRoundToStep is set to true, because
    // we have to have some limit on the precision outside of manual entry
    const shouldDisableRoundToStep = this._disableRoundToStep && (this._isDateType || !didDrag || value === this._scale.minValue || value === this._scale.maxValue);

    // get the precise value based on step
    return calculatePreciseValue(value, this._step, this._numberFormat, shouldDisableRoundToStep);
  }
  get minValue() {
    return this._scale.minValue;
  }
  get maxValue() {
    return this._scale.maxValue;
  }
  position(value) {
    return this._scale.position(value);
  }
}
const getCurrentCustomTickLabel = (currValue, ticks, tickIds) => {
  const indexOfTick = ticks.indexOf(currValue);
  return tickIds[indexOfTick];
};

/**
 * This component has limited support for multiple ranges and values. Those cases aren't
 * well tested and probably have some issues.
 */
export const BaseSlider = /*#__PURE__*/forwardRef(function BaseSlider({
  ...props
}, forwardedRef) {
  const localize = useNCTextLocalizer('Slider');
  const orientation = props.orientation || 'horizontal';
  const arrowGrips = props.arrowGrips ?? false;
  const formatLocale = useFormatLocale();
  const defaultFormatter = new SliderDefaultFormat(formatLocale, {
    maximumFractionDigits: 20
  }, props.disableRoundToStep);
  const formatter = props.format || defaultFormatter;
  const scale = useMemo(() => {
    const numberFormat = new Intl.NumberFormat(formatLocale, {
      maximumFractionDigits: 20
    });
    const sliderStep = props.step ?? 1;
    let sliderScale;
    if (props.scale === undefined) {
      sliderScale = defaultScale;
    } else if (Array.isArray(props.scale)) {
      sliderScale = LinearSliderScale.createFromMinMax(
      // calculate precise values for scale min and max based on step
      calculatePreciseValue(props.scale[0], sliderStep, numberFormat, props.disableRoundToStep), calculatePreciseValue(props.scale[1], sliderStep, numberFormat, props.disableRoundToStep), props.isDateType ?? false);
    } else {
      sliderScale = props.scale;
    }
    return new SnapPrecisionScale(sliderScale, props.step ?? 1, numberFormat, props.disableRoundToStep, props.isDateType);
  }, [props.scale, props.step, formatLocale, props.disableRoundToStep, props.isDateType]);
  const step = props.step ?? 1;
  const stepMedium = props.stepMedium ?? calculateSliderStep('medium', step, scale);
  const stepLarge = props.stepLarge ?? calculateSliderStep('large', step, scale);
  const isDisabled = props.disabled || scale.minValue === scale.maxValue && !props.enableSliderWithSingleScaleValue;
  const isReadOnly = props.readOnly || scale.minValue === scale.maxValue && props.enableSliderWithSingleScaleValue;
  const ticks = getSliderTicks(scale, props.ticks, props.tickLabels?.length, step);
  const tickIds = useHtmlId(ticks.length);
  const sliderRef = useRef(null);
  const sliderTickLabelsRef = useRef(null);
  const sliderValueGripRef = useRef(null);
  let valueSliderType = undefined;
  useImperativeHandle(forwardedRef, () => ({
    focus() {
      sliderValueGripRef.current?.focus();
    }
  }));
  const [sliderResizeRef] = useResizeObserverRef(() => {
    if (props.orientation === 'vertical' && sliderTickLabelsRef.current) {
      const sliderWidth = sliderRef.current?.getBoundingClientRect().width;
      const sliderGripWidth = sliderValueGripRef.current?.getGripContainerWidth() ?? 0;
      const sliderGripLabelWidth = sliderValueGripRef.current?.getGripLabelElementWidth() ?? 0;
      if (sliderWidth) {
        const maxLabelsWidth = sliderWidth - sliderGripWidth - sliderGripLabelWidth;
        sliderTickLabelsRef.current.style.maxWidth = `${maxLabelsWidth}px`;
      }
    }
  });
  setRef(sliderResizeRef, sliderRef.current);
  const getSliderClientRect = () => {
    return sliderRef.current?.getBoundingClientRect();
  };

  /**
   * - `end` => the end grip in a range
   * - `start` => the start grip in a range
   * - `single` => the grip for a single value (not a range)
   */

  const [lastDraggedHandleIndex, setLastDraggedHandleIndex] = useState([-1, 'single']);
  const refTrack = useRef(null);
  const clientBounds = {
    getSize() {
      if (!refTrack.current) {
        return undefined;
      }
      const bounds = refTrack.current.getBoundingClientRect();
      return {
        width: bounds.width,
        height: bounds.height
      };
    }
  };
  const isRTL = useIsRTL();
  const onTrackClick = props.disabled || props.readOnly ? undefined : e => {
    if (!refTrack.current) {
      return undefined;
    }
    const trackBounds = refTrack.current.getBoundingClientRect();
    const clickPos = orientation === 'vertical' ? (trackBounds.bottom - e.clientY) / trackBounds.height : (isRTL ? trackBounds.right - e.clientX : e.clientX - trackBounds.left) / trackBounds.width;
    const clickValue = scale.value(clickPos);

    // find the closest grip
    let closestValueDelta = Infinity,
      closestValue = undefined,
      closestValueKey = 'value';
    for (const sliderValue of props.values) {
      if (sliderValue.type !== 'range') {
        const delta = Math.abs(sliderValue.value - clickValue);
        // S1548296: handle grips that have the same value by:
        //   favoring later grips with the same delta distance if the value is larger.
        if (delta < closestValueDelta || delta === closestValueDelta && clickValue > sliderValue.value) {
          closestValueDelta = delta;
          closestValue = sliderValue;
          closestValueKey = 'value';
        }
      } else {
        const minDelta = Math.abs(sliderValue.minValue - clickValue),
          maxDelta = Math.abs(sliderValue.maxValue - clickValue);
        // S1548296: handle grips that have the same value by:
        //   favoring later grips with the same delta distance if the value is larger.
        if (minDelta < closestValueDelta || minDelta === closestValueDelta && clickValue > sliderValue.minValue) {
          closestValueDelta = minDelta;
          closestValue = sliderValue;
          closestValueKey = 'minValue';
        }
        if (maxDelta < closestValueDelta || maxDelta === closestValueDelta && clickValue > sliderValue.maxValue) {
          closestValueDelta = maxDelta;
          closestValue = sliderValue;
          closestValueKey = 'maxValue';
        }
      }
    }
    if (closestValue !== undefined) {
      if (closestValue.type !== 'range') {
        closestValue.onChange({
          value: clickValue
        });
        if (closestValue.onCommit) {
          closestValue.onCommit({
            value: clickValue
          });
        }
      } else {
        closestValue.onChange({
          minValue: closestValueKey === 'minValue' ? clickValue : closestValue.minValue,
          maxValue: closestValueKey === 'maxValue' ? clickValue : closestValue.maxValue
        });
        if (closestValue.onCommit) {
          closestValue.onCommit({
            minValue: closestValueKey === 'minValue' ? clickValue : closestValue.minValue,
            maxValue: closestValueKey === 'maxValue' ? clickValue : closestValue.maxValue
          });
        }
      }
    }
  };
  const valueElements = props.values.map((value, index) => {
    const [lowerBoundValue, upperBoundValue] = resolveGripBoundary(props.values[index - 1], props.values[index + 1], scale);
    valueSliderType = value.valueType;
    const shouldHideInput = !!props.hideValueLabel || !!props.hideInput || !!props.disabled || !!props.readOnly;
    if (value.type !== 'range') {
      let zIndex = 1;
      if (lastDraggedHandleIndex[0] === index && lastDraggedHandleIndex[1] === 'single') {
        zIndex = 2;
      }
      const numberFormat = new Intl.NumberFormat(formatLocale, {
        maximumFractionDigits: 20
      });
      // calculate precise value for based on step
      const newValue = calculatePreciseValue(value.value, props.step ?? 1, numberFormat, props.disableRoundToStep);
      // use the calculated value to adjust according to the min and max allowed by the scale
      const adjustedValue = adjustValueForScale(newValue, lowerBoundValue, upperBoundValue);
      const thumbAriaLabel = (() => {
        // if there is an ariaLabelledby, or there is no ariaLabel
        // use only the gripAriaText for the ariaLabel used in the grip's InvisibleText
        if (props.ariaLabelledBy || !props.ariaLabel) {
          return props.gripAriaText;
        }
        return props.gripAriaText ? `${props.ariaLabel}, ${props.gripAriaText}` : props.ariaLabel;
      })();
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [value.valueType !== SliderType.LessThan ? null : /*#__PURE__*/_jsx(SliderRange, {
          testId: postfixTestId(props.testId, 'range'),
          disabled: isDisabled,
          xStyle: {
            orientation,
            minPos: scale.position(lowerBoundValue),
            maxPos: scale.position(adjustedValue)
          },
          onClick: onTrackClick
        }), /*#__PURE__*/_jsx(SliderValueGrip, {
          setSliderGripControlIsVisible: props.setSliderGripControlIsVisible,
          testId: postfixTestId(props.testId, 'valueGrip'),
          disabled: isDisabled,
          readOnly: isReadOnly,
          enableSliderWithSingleScaleValue: props.enableSliderWithSingleScaleValue,
          value: adjustedValue,
          type: GripType.Single,
          minValue: lowerBoundValue,
          maxValue: upperBoundValue,
          inclusive: !value.exclusive,
          onChange: value.onChange,
          onCommit: value.onCommit,
          zIndex: zIndex,
          format: formatter,
          defaultFormat: defaultFormatter,
          disableRoundToStep: props.disableRoundToStep,
          clientBounds: clientBounds,
          scale: scale,
          orientation: orientation,
          hideLabel: !!props.hideValueLabel,
          hideInput: shouldHideInput,
          stepValue: step,
          stepMediumValue: stepMedium,
          stepLargeValue: stepLarge,
          gripLabelClassName: props.gripLabelClassName,
          renderGripInput: props.renderGripInput,
          handleLabelTokens: props.handleLabelTokens,
          onDragStart: props.disabled || props.readOnly ? undefined : () => setLastDraggedHandleIndex([index, 'single']),
          ariaLabelledBy: props.ariaLabelledBy,
          ariaLabel: thumbAriaLabel,
          ariaDescribedBy: getCurrentCustomTickLabel(value.value, ticks, tickIds),
          arrowGrips: arrowGrips,
          getSliderClientRect: getSliderClientRect,
          ref: sliderValueGripRef,
          sliderHasTicks: !!props.ticks
        }), value.valueType !== SliderType.GreaterThan ? null : /*#__PURE__*/_jsx(SliderRange, {
          testId: postfixTestId(props.testId, 'range'),
          disabled: isDisabled,
          xStyle: {
            orientation,
            minPos: scale.position(adjustedValue),
            maxPos: scale.position(upperBoundValue)
          },
          onClick: onTrackClick
        })]
      }, index);
    } else {
      const numberFormat = new Intl.NumberFormat(formatLocale, {
        maximumFractionDigits: 20
      });
      // calculate precise values based on step
      const newMinValue = calculatePreciseValue(value.minValue, props.step ?? 1, numberFormat, props.disableRoundToStep);
      const newMaxValue = calculatePreciseValue(value.maxValue, props.step ?? 1, numberFormat, props.disableRoundToStep);

      // use the calculated values to adjust according to the min and max allowed by the scale
      const adjustedMinValue = adjustValueForScale(newMinValue, lowerBoundValue, upperBoundValue);
      const adjustedMaxValue = adjustValueForScale(newMaxValue, lowerBoundValue, upperBoundValue);
      let startZ = 1,
        endZ = 1;
      if (lastDraggedHandleIndex[1] === 'end') {
        endZ = 2;
      } else if (lastDraggedHandleIndex[1] === 'start') {
        startZ = 2;
      }
      const minThumbText = props.minGripAriaText ?? localize('sliderGrip.sliderMinThumb.aria');
      const minThumbAriaLabel = (() => {
        // if there is an ariaLabelledby, or there is no ariaLabel
        // use only the minThumbText for the ariaLabel used in the grip's InvisibleText
        if (props.ariaLabelledBy || !props.ariaLabel) {
          return minThumbText;
        }
        return `${props.ariaLabel}, ${minThumbText}`;
      })();
      const maxThumbText = props.maxGripAriaText ?? localize('sliderGrip.sliderMaxThumb.aria');
      const maxThumbAriaLabel = (() => {
        // if there is an ariaLabelledby, or there is no ariaLabel
        // use only maxThumbText for the ariaLabel used in the grip's InvisibleText
        if (props.ariaLabelledBy || !props.ariaLabel) {
          return maxThumbText;
        }
        return `${props.ariaLabel}, ${maxThumbText}`;
      })();
      const barText = props.rangeAriaText ?? localize('sliderRangeGrip.sliderRange.aria');
      const barAriaLabel = (() => {
        // if there is an ariaLabelledby, or there is no ariaLabel
        // use only barText for the ariaLabel used in the bar's InvisibleText
        if (props.ariaLabelledBy || !props.ariaLabel) {
          return barText;
        }
        return `${props.ariaLabel}, ${barText}`;
      })();
      return /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(SliderValueGrip, {
          setSliderGripControlIsVisible: props.setSliderGripControlIsVisible,
          setHasSliderInputErrorOrFocus: props.setHasMinSliderInputErrorOrFocus,
          testId: postfixTestId(props.testId, 'minValueGrip'),
          disabled: isDisabled,
          readOnly: isReadOnly,
          enableSliderWithSingleScaleValue: props.enableSliderWithSingleScaleValue,
          value: adjustedMinValue,
          minValue: lowerBoundValue,
          maxValue: adjustedMaxValue,
          type: GripType.Minimum,
          inclusive: !value.minExclusive,
          zIndex: startZ,
          format: formatter,
          defaultFormat: defaultFormatter,
          disableRoundToStep: props.disableRoundToStep,
          scale: scale,
          clientBounds: clientBounds,
          orientation: orientation,
          isRange: true,
          isDateType: props.isDateType,
          hideLabel: !!props.hideValueLabel,
          hideInput: shouldHideInput,
          stepValue: step,
          stepMediumValue: stepMedium,
          stepLargeValue: stepLarge,
          gripLabelClassName: props.gripLabelClassName,
          renderGripInput: props.renderGripInput,
          handleLabelTokens: props.handleLabelTokens,
          ariaLabel: minThumbAriaLabel,
          ariaLabelledBy: props.ariaLabelledBy,
          arrowGrips: arrowGrips,
          onDragStart: props.disabled || props.readOnly ? undefined : () => setLastDraggedHandleIndex([index, 'start']),
          onChange: props.disabled || props.readOnly ? undefined : e => {
            value.onChange({
              minValue: e.value,
              maxValue: adjustedMaxValue
            });
          },
          onCommit: props.disabled || props.readOnly ? undefined : e => {
            if (value.onCommit) {
              value.onCommit({
                minValue: e.value,
                maxValue: adjustedMaxValue
              });
            }
          },
          getSliderClientRect: getSliderClientRect
        }), /*#__PURE__*/_jsx(SliderRangeGrip, {
          sliderGripControlIsVisible: props.sliderGripControlIsVisible,
          testId: postfixTestId(props.testId, 'rangeGrip'),
          disabled: isDisabled,
          readOnly: isReadOnly,
          enableSliderWithSingleScaleValue: props.enableSliderWithSingleScaleValue,
          startValue: adjustedMinValue,
          endValue: adjustedMaxValue,
          minValue: lowerBoundValue,
          maxValue: upperBoundValue,
          format: formatter,
          disableRoundToStep: props.disableRoundToStep,
          hideLabel: !!props.hideValueLabel,
          scale: scale,
          clientBounds: clientBounds,
          orientation: orientation,
          showExclusive: valueSliderType === RangeSliderType.Exclusive,
          stepValue: step,
          stepMediumValue: stepMedium,
          stepLargeValue: stepLarge,
          gripLabelClassName: props.gripLabelClassName,
          handleLabelTokens: props.handleLabelTokens,
          ariaLabelledBy: props.ariaLabelledBy,
          ariaLabel: barAriaLabel,
          onPress: onTrackClick,
          onChange: props.disabled || props.readOnly ? undefined : e => {
            value.onChange({
              minValue: e.startValue,
              maxValue: e.endValue
            });
          },
          onCommit: props.disabled || props.readOnly ? undefined : e => {
            if (value.onCommit) value.onCommit({
              minValue: e.startValue,
              maxValue: e.endValue
            });
          }
        }), /*#__PURE__*/_jsx(SliderValueGrip, {
          setSliderGripControlIsVisible: props.setSliderGripControlIsVisible,
          setHasSliderInputErrorOrFocus: props.setHasMaxSliderInputErrorOrFocus,
          testId: postfixTestId(props.testId, 'maxValueGrip'),
          disabled: isDisabled,
          readOnly: isReadOnly,
          enableSliderWithSingleScaleValue: props.enableSliderWithSingleScaleValue,
          value: adjustedMaxValue,
          minValue: adjustedMinValue,
          maxValue: upperBoundValue,
          type: GripType.Maximum,
          inclusive: !value.maxExclusive,
          zIndex: endZ,
          format: formatter,
          defaultFormat: defaultFormatter,
          disableRoundToStep: props.disableRoundToStep,
          scale: scale,
          clientBounds: clientBounds,
          orientation: orientation,
          isRange: true,
          isDateType: props.isDateType,
          hideLabel: !!props.hideValueLabel,
          hideInput: shouldHideInput,
          stepValue: step,
          stepMediumValue: stepMedium,
          stepLargeValue: stepLarge,
          gripLabelClassName: props.gripLabelClassName,
          renderGripInput: props.renderGripInput,
          handleLabelTokens: props.handleLabelTokens,
          ariaLabel: maxThumbAriaLabel,
          ariaLabelledBy: props.ariaLabelledBy,
          arrowGrips: arrowGrips,
          onDragStart: props.disabled || props.readOnly ? undefined : () => setLastDraggedHandleIndex([index, 'end']),
          onChange: props.disabled || props.readOnly ? undefined : e => {
            value.onChange({
              minValue: adjustedMinValue,
              maxValue: e.value
            });
          },
          onCommit: props.disabled || props.readOnly ? undefined : e => {
            value.onCommit && value.onCommit({
              minValue: adjustedMinValue,
              maxValue: e.value
            });
          },
          getSliderClientRect: getSliderClientRect
        })]
      }, index);
    }
  });
  const dt = useDesignTokens();
  const tokens = {
    ...getSliderDesignTokens(dt, {
      gripLabelOverrides: props.handleLabelTokens,
      tickLabelOverrides: props.tickLabelTokens,
      valueType: props.values[0].valueType,
      disabled: isDisabled
    }),
    ...props.theme?.(dt)
  };
  return /*#__PURE__*/_jsxs("div", {
    className: cx(cn.container[orientation], !props.ticks && !props.tickLabels && cn.noTicks, props.className),
    style: {
      ...tokens,
      ...props.style
    },
    "data-testid": props.testId,
    id: props.id,
    ref: sliderRef,
    children: [/*#__PURE__*/_jsx(SliderTickLabels, {
      testId: postfixTestId(props.testId, 'tickLabels'),
      disabled: isDisabled,
      ticks: ticks,
      tickIds: tickIds,
      tickLabels: props.tickLabels,
      format: formatter,
      scale: scale,
      orientation: orientation,
      labelTokens: props.tickLabelTokens,
      ref: sliderTickLabelsRef
    }), /*#__PURE__*/_jsx("div", {
      className: cx(cn.track[orientation], valueSliderType === RangeSliderType.Exclusive ? props.className : props.trackClassName),
      ref: refTrack,
      style: props.trackStyle,
      "data-testid": postfixTestId(props.testId, 'track'),
      onClick: onTrackClick,
      children: /*#__PURE__*/_jsx(SliderTrackTicks, {
        testId: postfixTestId(props.testId, 'trackTicks'),
        orientation: orientation,
        tickPositions: ticks.map((v, index, array) => {
          if (v === scale.maxValue && index === array.length - 1) {
            // NOTE: handles case when scale.minValue === scale.maxValue
            return 1;
          }
          return scale.position(v);
        })
      })
    }), valueElements]
  });
});
function resolveGripBoundary(left, right, scale) {
  const bounds = [0, 0];
  if (left === undefined) {
    bounds[0] = scale.minValue;
  } else if (left.type === 'range') {
    bounds[0] = left.maxValue;
  } else {
    bounds[0] = left.value;
  }
  if (right === undefined) {
    bounds[1] = scale.maxValue;
  } else if (right.type === 'range') {
    bounds[1] = right.minValue;
  } else {
    bounds[1] = right.value;
  }
  return bounds;
}
function adjustValueForScale(value, lowerBoundValue, upperBoundValue) {
  let adjustedValue = Math.max(value, lowerBoundValue);
  adjustedValue = Math.min(adjustedValue, upperBoundValue);
  return adjustedValue;
}
export const getSliderDesignTokens = (dt, opts) => {
  return {
    ...renderDesignTokens({
      'spacing-xs': dt.tokens['space-base-xs']
    }, 'slider'),
    ...renderDesignTokens({
      bd: opts.disabled ? dt.tokens['color-action-border-on-off-disabled'] : dt.tokens['color-container-border-4'],
      bg: opts.valueType === RangeSliderType.Exclusive ? dt.tokens['color-action-background-on-off-selected'] : dt.tokens['color-container-background-0'],
      'top-bd': opts.disabled ? dt.tokens['color-action-border-on-off-disabled'] : dt.tokens['color-container-border-4']
    }, 'slider-track'),
    ...renderDesignTokens({
      'spacing-s': dt.tokens['space-base-s'],
      'spacing-l': dt.tokens['space-base-l'],
      'spacing-xl': dt.tokens['space-base-xl'],
      'spacing-xxl': dt.tokens['space-base-xxl']
    }, 'slider-datepicker'),
    ...renderDesignTokens({
      'spacing-squish-xs': `${dt.tokens['space-base-xxs']} ${dt.tokens['space-base-xs']}`,
      'font-size-m': dt.tokens['font-size-body-text-m']
    }, 'slider-value-grip'),
    ...renderDesignTokens({
      bg: dt.tokens['color-action-background-on-off-normal'],
      bd: opts.disabled ? dt.tokens['color-action-border-on-off-disabled'] : dt.tokens['color-action-border-on-off-normal'],
      'disabled-bd': dt.tokens['color-action-border-on-off-disabled'],
      'indicator-bg': dt.tokens['color-action-background-on-off-selected'],
      'spacing-s': dt.tokens['space-base-s'],
      'indicator-disabled-bg': dt.tokens['color-action-background-on-off-disabled']
    }, 'slider-grip'),
    ...renderDesignTokens({
      'spacing-xs': dt.tokens['space-base-xs'],
      'font-size-s': dt.tokens['font-size-body-text-s'],
      fg: dt.tokens['color-body-text'],
      'disabled-fg': dt.tokens['color-action-text-control-disabled'],
      ...opts.tickLabelOverrides?.font,
      ...opts.tickLabelOverrides?.theme?.()
    }, 'slider-tick-label'),
    ...renderDesignTokens({
      'spacing-xs': dt.tokens['space-base-xs'],
      'spacing-m': dt.tokens['space-base-m'],
      'font-size-m': dt.tokens['font-size-body-text-m'],
      fg: dt.tokens['color-action-text-control-normal'],
      'disabled-fg': dt.tokens['color-action-text-control-disabled'],
      ...opts.gripLabelOverrides?.font,
      ...opts.gripLabelOverrides?.theme?.()
    }, 'slider-grip-label'),
    ...renderDesignTokens({
      bg: dt.tokens['color-action-background-control-normal']
    }, 'slider-datepicker'),
    ...renderDesignTokens({
      fg: opts.disabled ? dt.tokens['color-action-border-on-off-disabled'] : dt.tokens['color-container-border-4']
    }, 'slider-track-ticks'),
    ...(!dt.enableNovaTokens && getSliderLegacyDesignTokens(dt, opts))
  };
};
export const getSliderLegacyDesignTokens = (dt, opts) => {
  let tickLabelFg;
  let gripLabelFg;
  switch (dt.themeVariant) {
    case 'high-contrast':
      tickLabelFg = dt.c.n2;
      gripLabelFg = dt.c.white;
      break;
    case 'dark':
      tickLabelFg = dt.c.white;
      gripLabelFg = dt.c.white;
      break;
    default:
      tickLabelFg = dt.c.n_1;
      gripLabelFg = dt.c.n_1;
      break;
  }
  switch (dt.baseReportThemeName) {
    case 'highcontrast2018':
    case 'highcontrast2017':
      tickLabelFg = {
        fg: dt.c.white
      };
      return {
        ...renderDesignTokens({
          bg: opts.valueType === RangeSliderType.Exclusive ? dt.reportTokens.stateSecondaryToggle.selected.bg : dt.c.black,
          bd: dt.c.white,
          'top-bd': dt.c.white
        }, 'slider-track'),
        ...renderDesignTokens({
          'font-size-m': dt.reportTokens.font.size.m
        }, 'slider-value-grip'),
        ...renderDesignTokens({
          bg: dt.reportTokens.stateSecondaryToggle.normal.bg,
          bd: dt.reportTokens.stateSecondaryToggle.normal.bd,
          'indicator-bg': dt.reportTokens.stateSecondaryToggle.selected.bg
        }, 'slider-grip'),
        ...renderDesignTokens({
          bg: dt.reportTokens.backgrounds.panes.bg
        }, 'slider-datepicker'),
        ...renderDesignTokens({
          fg: dt.c.white
        }, 'slider-track-ticks')
      };
  }
  switch (dt.themeVariant) {
    case 'high-contrast':
      return {
        ...renderDesignTokens({
          bg: opts.valueType === RangeSliderType.Exclusive ? dt.reportTokens.stateSecondaryToggle.selected.bg : dt.c.n_5,
          // n_6 in livingston?
          bd: dt.c.n2,
          'top-bd': dt.c.n2
        }, 'slider-track'),
        ...renderDesignTokens({
          'font-size-m': dt.reportTokens.font.size.m
        }, 'slider-value-grip'),
        ...renderDesignTokens({
          bg: dt.reportTokens.stateSecondaryToggle.normal.bg,
          bd: dt.reportTokens.stateSecondaryToggle.normal.bd,
          'indicator-bg': dt.reportTokens.stateSecondaryToggle.selected.bg
        }, 'slider-grip'),
        ...renderDesignTokens({
          bg: dt.reportTokens.backgrounds.panes.bg
        }, 'slider-datepicker'),
        ...renderDesignTokens({
          fg: dt.c.n2
        }, 'slider-track-ticks')
      };
    case 'dark':
      return {
        ...renderDesignTokens({
          bg: opts.valueType === RangeSliderType.Exclusive ? dt.reportTokens.stateSecondaryToggle.selected.bg : dt.c.p_3,
          bd: dt.c.p_2,
          'top-bd': dt.c.p_2
        }, 'slider-track'),
        ...renderDesignTokens({
          'font-size-m': dt.reportTokens.font.size.m
        }, 'slider-value-grip'),
        ...renderDesignTokens({
          bg: dt.reportTokens.stateSecondaryToggle.normal.bg,
          bd: dt.reportTokens.stateSecondaryToggle.normal.bd,
          'indicator-bg': dt.reportTokens.stateSecondaryToggle.selected.bg
        }, 'slider-grip'),
        ...renderDesignTokens({
          bg: dt.reportTokens.backgrounds.panes.bg
        }, 'slider-datepicker'),
        ...renderDesignTokens({
          fg: setAlpha(dt.c.white, 0.3)
        }, 'slider-track-ticks')
      };
    default:
      return {
        ...renderDesignTokens({
          bg: opts.valueType === RangeSliderType.Exclusive ? dt.reportTokens.stateSecondaryToggle.selected.bg : dt.c.n5,
          bd: dt.c.n3,
          'top-bd': dt.c.n_1
        }, 'slider-track'),
        ...renderDesignTokens({
          'font-size-m': dt.reportTokens.font.size.m
        }, 'slider-value-grip'),
        ...renderDesignTokens({
          bg: dt.reportTokens.stateSecondaryToggle.normal.bg,
          bd: dt.reportTokens.stateSecondaryToggle.normal.bd,
          'indicator-bg': dt.reportTokens.stateSecondaryToggle.selected.bg
        }, 'slider-grip'),
        ...renderDesignTokens({
          fg: tickLabelFg,
          'font-size-s': dt.reportTokens.font.size.s,
          'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
          ...opts.tickLabelOverrides?.font,
          ...opts.tickLabelOverrides?.theme?.()
        }, 'slider-tick-label'),
        ...renderDesignTokens({
          fg: gripLabelFg,
          'font-size-m': dt.reportTokens.font.size.m,
          'disabled-fg': dt.reportTokens.stateStandard.disabled.fg,
          ...opts.gripLabelOverrides?.font,
          ...opts.gripLabelOverrides?.theme?.()
        }, 'slider-grip-label'),
        ...renderDesignTokens({
          bg: dt.reportTokens.backgrounds.panes.bg
        }, 'slider-datepicker'),
        ...renderDesignTokens({
          fg: dt.c.n_1
        }, 'slider-track-ticks')
      };
  }
};
//# sourceMappingURL=Slider.js.map